// Head-to-head art for a match: each team's logo on its own color, split by
// a clean diagonal line that fades out at both ends, over a faded
// Predecessor background. `xl` is the match page's banner, with the logos
// large and frameless.
import { useId } from 'react'
import { safeAccent } from '@pred-hub/shared/color'
import { pickBackground } from '../../lib/backgrounds.js'
import { cn } from '../../lib/format.js'
import { TeamMark } from './TeamMark.jsx'

// The split runs from top-right of centre to bottom-left of centre.
const SPLIT = [
  [228, 0],
  [172, 200],
]
const line = SPLIT.map(([x, y]) => `${x},${y}`).join(' ')
const leftSide = `0,0 ${line} 0,200`
const rightSide = `${line} 400,200 400,0`

export function MatchupArt({ team1, team2, seed = 'match', size = 'md', className, children }) {
  const id = useId().replace(/:/g, '')
  const photo = pickBackground('pages', seed)
  const a = safeAccent(team1?.accent)
  const b = safeAccent(team2?.accent)

  return (
    <div className={cn('relative isolate overflow-hidden', className)}>
      {photo && (
        <img
          src={photo.src}
          alt=""
          aria-hidden="true"
          style={{ objectPosition: photo.position }}
          className="absolute inset-0 -z-20 size-full object-cover opacity-25 saturate-50"
        />
      )}
      <svg viewBox="0 0 400 200" preserveAspectRatio="none" aria-hidden="true" className="absolute inset-0 -z-10 size-full">
        <defs>
          <linearGradient id={`${id}-a`} x1="0" x2="1">
            <stop offset="0" stopColor={a} stopOpacity="0.45" />
            <stop offset="1" stopColor={a} stopOpacity="0.12" />
          </linearGradient>
          <linearGradient id={`${id}-b`} x1="1" x2="0">
            <stop offset="0" stopColor={b} stopOpacity="0.45" />
            <stop offset="1" stopColor={b} stopOpacity="0.12" />
          </linearGradient>
          {/* Gray, brightest in the middle and fading toward both ends. */}
          <linearGradient id={`${id}-line`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="200">
            <stop offset="0" stopColor="#d4dbe6" stopOpacity="0.15" />
            <stop offset="0.5" stopColor="#d4dbe6" stopOpacity="0.9" />
            <stop offset="1" stopColor="#d4dbe6" stopOpacity="0.15" />
          </linearGradient>
        </defs>
        <rect width="400" height="200" fill="#0d1426" fillOpacity="0.55" />
        <polygon points={leftSide} fill={`url(#${id}-a)`} />
        <polygon points={rightSide} fill={`url(#${id}-b)`} />
        {/* A soft band behind a firm line, so the split reads clearly. */}
        <polyline points={line} fill="none" stroke={`url(#${id}-line)`} strokeWidth="10" strokeOpacity="0.18" vectorEffect="non-scaling-stroke" />
        <polyline points={line} fill="none" stroke={`url(#${id}-line)`} strokeWidth="3" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="grid h-full grid-cols-2 items-center">
        {[team1, team2].map((team, i) => (
          <div key={i} className="flex justify-center">
            {size === 'xl' ? (
              <TeamMark team={team} size="hero" bare className="drop-shadow-[0_10px_18px_rgb(0_0_0/0.55)]" />
            ) : (
              <TeamMark team={team} size={size === 'lg' ? 'xl' : 'lg'} className="shadow-xl shadow-black/40" />
            )}
          </div>
        ))}
      </div>
      {children}
    </div>
  )
}
